"use client";

import posthog from "posthog-js";

interface ReportClientExceptionParams {
  /** Thrown value. Error instances get name + message + stack preserved. */
  error: unknown;
  /**
   * Stable identifier for where the error happened. Use values like
   * "site_error_boundary", "global_error_boundary", "email_wall_boundary".
   * Surfaces in Error Tracking under `capture_source` and lets us tell
   * which surface failed without reading code.
   */
  source: string;
  /**
   * Extra properties to attach. Useful for component-specific context (the
   * variant being rendered, the story slug, etc.). Avoid PII.
   */
  extra?: Record<string, unknown>;
}

/**
 * Forward a caught/boundary-rendered exception to PostHog Error Tracking.
 *
 * Wraps `posthog.captureException` so every reporter passes a `capture_source`
 * tag and so we have one place to add filtering, sampling, or fallback
 * logging in the future. Callers should never need to import posthog-js
 * directly for error reporting.
 *
 * Safe to call from anywhere — failures inside this helper are swallowed so
 * a broken analytics call can never cascade into a second render error.
 */
export const reportClientException = ({
  error,
  source,
  extra,
}: ReportClientExceptionParams): void => {
  try {
    if (typeof window === "undefined") {
      return;
    }

    const properties = {
      capture_source: source,
      ...(extra ?? {}),
    };

    if (error instanceof Error) {
      posthog.captureException(error, properties);
      return;
    }

    // Non-Error throws (strings, plain objects, …) — wrap so PostHog still
    // gets a structured exception event instead of swallowing the call.
    const wrapped = new Error(
      typeof error === "string" ? error : "Non-Error value thrown"
    );
    posthog.captureException(wrapped, {
      ...properties,
      original_value: safeStringify(error),
    });
  } catch {
    // Swallow — error reporting must not throw.
  }
};

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};
